import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, UserPlus, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable insurance page — theme + benefits table are supplied by the caller.
 * Layout follows the "locked" hero + two-requirement progress + benefits table.
 */
export default function InsurancePage({
  title,
  themeColor, // e.g. { header: "#dc2626", darker: "#b91c1c", tint: "#fef2f2" }
  headline,
  benefits, // [{ rank, value }]
  testId,
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/insurance-status");
        setStatus(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const required = status?.required ?? 5;
  const activeCount = Math.min(status?.active_directs ?? 0, required);
  const promoterCount = Math.min(status?.promoter_directs ?? 0, required);
  const unlocked = !!status?.unlocked;

  const pct1 = Math.min(100, (activeCount / required) * 100);
  const pct2 = Math.min(100, (promoterCount / required) * 100);

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main
        data-testid={testId}
        className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-16 text-slate-900"
      >
        <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
          {/* Hero */}
          <div
            className="px-6 py-8 text-center text-white"
            style={{ background: themeColor.header }}
            data-testid={`${testId}-hero`}
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
              {unlocked ? (
                <ShieldCheck size={28} className="text-white" />
              ) : (
                <Lock size={26} className="text-white" />
              )}
            </div>
            <h1
              className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {title} {unlocked ? "Unlocked" : "Locked"}
            </h1>
            <p className="mt-1 text-sm text-white/85">
              {unlocked
                ? "Congratulations! Your insurance benefits are active."
                : "Complete both requirements below to unlock"}
            </p>
          </div>

          {/* Requirements */}
          <div className="p-5 space-y-4">
            {/* Req 1 */}
            <div
              data-testid="req-active-directs"
              className="rounded-xl border border-slate-200 p-4 bg-slate-50/40"
            >
              <div className="flex items-center gap-3">
                <span
                  className="h-7 w-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
                  style={{ background: activeCount >= required ? "#059669" : "#94a3b8" }}
                >
                  1
                </span>
                <div className="font-bold text-slate-900">
                  {required} Active Direct Referrals
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${pct1}%`,
                    background: activeCount >= required ? "#059669" : themeColor.header,
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-slate-600">
                <b>{activeCount}</b> / {required} Active direct referrals
              </div>
            </div>

            {/* Req 2 */}
            <div
              data-testid="req-promoter-directs"
              className="rounded-xl border border-slate-200 p-4 bg-slate-50/40"
            >
              <div className="flex items-center gap-3">
                <span
                  className="h-7 w-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
                  style={{ background: promoterCount >= required ? "#059669" : "#94a3b8" }}
                >
                  2
                </span>
                <div className="font-bold text-slate-900">
                  {required} Direct Referrals reach{" "}
                  <span style={{ color: themeColor.header }}>Promoter</span> rank
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${pct2}%`,
                    background: promoterCount >= required ? "#059669" : themeColor.header,
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-slate-600">
                <b>{promoterCount}</b> / {required} direct referrals reached Promoter rank
              </div>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                A direct referral reaches Promoter rank when they have 2 Active direct
                referrals of their own.
              </p>
            </div>

            {/* Benefits */}
            <div
              data-testid="benefits-table"
              className="rounded-xl border p-4"
              style={{ borderColor: themeColor.header + "33", background: themeColor.tint }}
            >
              <div
                className="text-xs font-extrabold tracking-widest uppercase mb-2"
                style={{ color: themeColor.header }}
              >
                What You Will Unlock
              </div>
              <div className="divide-y divide-white/40">
                {benefits.map((row) => (
                  <div
                    key={row.rank}
                    className="flex items-start justify-between gap-3 py-2 text-sm"
                  >
                    <span className="text-slate-700">{row.rank}</span>
                    <span className="text-slate-900 font-semibold text-right">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            {!unlocked && (
              <Link
                to="/referrals"
                data-testid="cta-invite-refer"
                className="mt-2 flex items-center justify-center gap-2 rounded-xl py-3.5 text-white font-bold shadow-lg active:scale-[0.99] transition-transform"
                style={{ background: themeColor.header }}
              >
                <UserPlus size={18} /> Invite &amp; Refer Now
              </Link>
            )}
            {loading && (
              <div className="text-center text-xs text-slate-500 py-2">Loading status…</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
